/**
 * The right-hand brief comments column: real brief comment threads backed by
 * the target-based comment store plus the brief comment hooks from
 * `features/comment-on-file`.
 *
 * Scoping: the comment store is target-based. On mount / scope change we seed
 * it with the brief target (`initCommentsStore({ targetType: 'brief', ... })`)
 * so the read hook (`useBriefComments`) and the composer
 * (`AnnotatedCommentInput`, which reads `useCommentsScope` for attachment
 * uploads) resolve against this brief.
 *
 * Briefs are NOT wrapped in a `<MediaPlayerProvider>`, so we render
 * `BriefCommentThread` (no annotation/media hooks) and pass
 * `showAnnotationToolbar={false}` to the composer. The injected `actions` adapt
 * the brief hooks to the `CommentThreadActions` contract. Brief comments carry
 * a `text` annotation (the highlight anchor) rather than an overlay shape.
 *
 * Exports `BriefCommentsSidebarBody` only: the content with no outer Card,
 * header, or title. The chrome belongs to the caller — `BriefRightPanel` hosts
 * this inside `DetailSidebar`'s card, which owns the header and the scroll box.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { CardContent, Spinner } from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import type {
  BriefResponseDto,
  CommentResponseDto,
} from '@inference.engine/workspace-api-client/web';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { isProjectOwner, projectQuery } from '@/entities/project';
import { useCurrentUserId } from '@/entities/user';
import { notificationService } from '@/lib/services/notification.service';
import { ScopedPermission } from '@/shared/ui';
import {
  AnnotatedCommentInput,
  BriefCommentThread,
  clearCommentsStore,
  CommentsPanel,
  HighlightCommentComposerChip,
  initCommentsStore,
  useBriefComments,
  useCommentsScope,
  useCreateBriefComment,
  useDeleteBriefComment,
  useDeselectOnOutsideClick,
  usePendingCommentScroll,
  usePendingFieldAnchor,
  usePendingTextAnchor,
  useResolveBriefComment,
  useRootSortOrder,
  useSelectedCommentId,
  useShowResolved,
  useUpdateBriefComment,
  type CommentThreadActions,
} from '@/features/comment-on-file';
import {
  SuggestionBlock,
  useAgentIdentityResolver,
  useAgentThreadResponder,
  useBriefMentionOptions,
  type CommentSuggestion,
} from '@/features/brief-agent-review';
import { CommentAnchorChip } from '@/entities/project-comment';
import { Trans, useTranslation } from 'react-i18next';
import { Rows3Icon } from 'lucide-react';

interface BriefCommentsSidebarProps {
  projectId: string;
  briefId: string;
  workspaceId: string;
  platformProjectId?: string | null;
}

interface BriefCommentsSidebarBodyProps extends BriefCommentsSidebarProps {
  /**
   * The loaded brief (agent suggestions POC). Enables the Apply action on
   * agent suggestion comments — Apply replaces the target section's content
   * via brief PATCH and needs ALL current sections (id-keyed diff: omitted
   * sections are soft-deleted). When absent, suggestion blocks don't render.
   */
  brief?: BriefResponseDto;
  /**
   * Fires after a suggestion's brief PATCH succeeds, with the applied section
   * content. The brief editor page uses it to sync its form state + remount
   * the section's TipTap editor — without it the applied content is persisted
   * but invisible on screen, and the next manual Save reverts it.
   */
  onSuggestionApplied?: (sectionId: string, content: Record<string, unknown>) => boolean;
}

/**
 * Headless brief-comments body: renders WITHOUT an outer Card/header, for
 * callers that supply their own chrome. The seed gate means scope-reading hooks
 * fire only once the store points at THIS brief.
 */
export function BriefCommentsSidebarBody({
  projectId,
  briefId,
  workspaceId,
  platformProjectId,
  brief,
  onSuggestionApplied,
}: BriefCommentsSidebarBodyProps) {
  // Seed the target-based store to this brief before the read hook / composer
  // resolve. Re-runs when any scope prop changes (e.g. route navigation to a
  // different brief).
  useEffect(() => {
    initCommentsStore({
      projectId,
      targetType: 'brief',
      targetId: briefId,
      workspaceId,
      platformProjectId,
    });
  }, [projectId, briefId, workspaceId, platformProjectId]);

  useEffect(() => () => clearCommentsStore(), []);

  // The store is a module-level singleton seeded by the effect above, which
  // fires AFTER first commit. Until it reflects THIS brief, any scope-reading
  // hook (the read query, the brief action hooks, the composer's attachment
  // uploads) would resolve against `''` (first ever mount) or the PREVIOUS
  // target (brief→brief / asset→brief navigation). So we gate the content:
  // `BriefCommentsContent` — which owns every scope-reading hook — only mounts
  // once the store is seeded to this brief. On a briefId change `seeded` goes
  // false then true, unmounting/remounting the content against the new target.
  const scope = useCommentsScope();
  const seeded = scope.targetType === 'brief' && scope.targetId === briefId;

  if (!seeded) {
    return (
      <CardContent className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-0">
        <div className="flex justify-center py-8">
          <Spinner className="size-5 text-muted-foreground" />
        </div>
      </CardContent>
    );
  }

  return (
    <BriefCommentsContent
      brief={brief}
      platformProjectId={platformProjectId}
      onSuggestionApplied={onSuggestionApplied}
    />
  );
}

/**
 * Seeded content. Mounts ONLY once the comment store scope points at this
 * brief, so every scope-reading hook below fires with the correct brief
 * `targetId` on its first render. Not exported — it's an implementation detail
 * of the body's seed gate.
 */
function BriefCommentsContent({
  brief,
  platformProjectId,
  onSuggestionApplied,
}: {
  brief?: BriefResponseDto;
  platformProjectId?: string | null;
  onSuggestionApplied?: (sectionId: string, content: Record<string, unknown>) => boolean;
}) {
  const { t } = useTranslation('projects');

  const { comments, totalAll, isLoading, hasMore, isFetchingMore, loadMore } = useBriefComments();
  const { createComment } = useCreateBriefComment();
  const { updateComment } = useUpdateBriefComment();
  const { deleteComment, isDeleting } = useDeleteBriefComment();
  const { resolveComment } = useResolveBriefComment();
  // Agent comments (POC) — maps stored agent id slugs to display names/avatars.
  const resolveAgentIdentity = useAgentIdentityResolver();

  // Mounted only when seeded, so scope === this brief.
  const scope = useCommentsScope();

  // Project owner may delete agent-authored comments (beyond authorship).
  const currentUserId = useCurrentUserId();
  const { data: project } = useQuery(projectQuery(scope.projectId));
  const projectOwner = isProjectOwner(project, currentUserId);

  // Taggable entities for the composer's `@` dropdown: ALL project
  // audiences + project members, all with avatars.
  const mentionOptions = useBriefMentionOptions({
    projectId: scope.projectId,
    platformProjectId,
  });

  // Agent thread responses (POC): after a human comment lands, an agent may
  // answer it (reply in an agent-rooted thread, or explicit @-tag).
  const { maybeRespond } = useAgentThreadResponder();
  const triggerAgentResponse = useCallback(
    (
      created: CommentResponseDto,
      parentId?: string,
      selection?: { selectedText: string; sectionId: string },
    ) => {
      if (!brief) return;
      const rootComment = parentId ? comments.find((c) => c.id === parentId) : created;
      if (!rootComment) return;
      // Replies hang off the root as a nested `replies` array — the root list is
      // roots-only, so filtering it by `parentId` always came back empty and the
      // agent lost the prior thread. Read the nested replies so it sees the
      // full conversation (the just-created comment is appended below).
      const replies = rootComment.replies ?? [];
      void maybeRespond({
        brief,
        platformProjectId: scope.projectId,
        newComment: created,
        root: rootComment,
        replies: [...replies, created],
        selection,
      });
    },
    [brief, comments, scope.projectId, maybeRespond],
  );

  // Apply an agent suggestion: insert the content into the section's live editor
  // (via the page handler) so it lands in the editor's undo history — it is undoable from the
  // keyboard and persists on the next brief Save. Then mark the comment resolved/Applied.
  //
  // The handler reports whether it actually wrote. A typed field can refuse a suggestion it
  // cannot hold, and it says so itself; resolving the comment as "applied" and raising a
  // success toast on top of that refusal told the user the opposite of what happened and
  // marked the suggestion applied for good. No handler means nothing was written either.
  const applySuggestion = useCallback(
    async (commentId: string, suggestion: CommentSuggestion) => {
      const wrote = onSuggestionApplied?.(suggestion.sectionId, suggestion.suggestedContent);
      if (!wrote) return;
      try {
        await resolveComment(commentId, 'resolved', 'applied');
        notificationService.showSuccess(
          t('projectBriefDetail.briefCommentsSidebar.suggestionApplied'),
        );
      } catch {
        notificationService.showError(
          t('projectBriefDetail.briefCommentsSidebar.suggestionAppliedButNotResolved'),
        );
      }
    },
    [resolveComment, onSuggestionApplied, t],
  );

  const ignoreSuggestion = useCallback(
    async (commentId: string) => {
      try {
        await resolveComment(commentId, 'resolved', 'ignored');
      } catch (error) {
        notificationService.showError(
          t('projectBriefDetail.briefCommentsSidebar.failedToIgnoreSuggestion'),
          { description: error instanceof Error ? error.message : undefined },
        );
      }
    },
    [resolveComment, t],
  );

  const { rootSortOrder, setRootSortOrder } = useRootSortOrder();
  const { showResolved, setShowResolved } = useShowResolved();
  const { pendingTextAnchor: anyTextAnchor, clearPendingTextAnchor } = usePendingTextAnchor();
  // An anchor with no target is an edit's re-anchoring draft, owned by that comment's edit
  // composer; this one composes new comments only.
  const pendingTextAnchor = anyTextAnchor?.targetId == null ? null : anyTextAnchor;
  const { pendingFieldAnchor, clearPendingFieldAnchor } = usePendingFieldAnchor();

  // One chip for either anchor, so the composer answers "what is this attached to?" the same
  // way whichever kind is pending. Text wins over field, matching the mutual-exclusion rule the
  // save path applies: a field has no document range to highlight.
  const pendingFieldSectionName = pendingFieldAnchor
    ? (brief?.sections.find((s) => s.id === pendingFieldAnchor.sectionId)?.name ?? null)
    : null;
  const composerAnchorChip = pendingTextAnchor ? (
    <HighlightCommentComposerChip onClear={pendingTextAnchor.cancelDraft} />
  ) : pendingFieldSectionName ? (
    <CommentAnchorChip
      icon={<Rows3Icon className="size-3 shrink-0 text-foreground-subtle" />}
      label={pendingFieldSectionName}
      ariaLabel={t('commentOnFile.sectionCommentComposerChip.commentWillBeAnchoredTo', {
        section: pendingFieldSectionName,
      })}
      onClear={clearPendingFieldAnchor}
      clearLabel={t('commentOnFile.sectionCommentComposerChip.removeSection')}
    />
  ) : undefined;
  const selectedCommentId = useSelectedCommentId();
  useDeselectOnOutsideClick();

  const commentRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const { setPendingScrollId, registerRow } = usePendingCommentScroll();

  useEffect(() => {
    if (!selectedCommentId) return;
    const el = commentRowRefs.current.get(selectedCommentId);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedCommentId]);

  // Only root comments seed threads; replies are owned by each `CommentThread`.
  const rootComments = useMemo(() => comments.filter((c) => !c.parentId), [comments]);
  const isEmpty = rootComments.length === 0;
  // Comments exist but none are visible ⇒ they're all resolved and hidden.
  const hasHiddenResolved = isEmpty && totalAll > 0;

  const actions: CommentThreadActions = useMemo(
    () => ({
      resolveSectionName: (sectionId: string) =>
        brief?.sections.find((s) => s.id === sectionId)?.name,
      createComment: async (content, options) => {
        const created = await createComment(content, {
          parentId: options?.parentId,
          attachmentAssetIds: options?.attachmentAssetIds,
        });
        triggerAgentResponse(created, options?.parentId);
      },
      updateComment: (commentId, content, options) =>
        updateComment(commentId, content, {
          ...(options && 'annotation' in options ? { annotation: options.annotation } : {}),
          attachmentAssetIds: options?.attachmentAssetIds,
        }),
      deleteComment,
      isDeleting,
      resolveComment,
      resolveAgentIdentity,
      canDeleteComment: (comment) => comment.authorType === 'agent' && projectOwner,
      renderCommentFooter: (comment) => {
        if (!comment.suggestion || !brief) return null;
        const suggestion = comment.suggestion as unknown as CommentSuggestion;
        return (
          <SuggestionBlock
            suggestion={suggestion}
            sectionExists={brief.sections.some((s) => s.id === suggestion.sectionId)}
            suggestionStatus={comment.suggestionStatus ?? null}
            onApply={(sugg) => applySuggestion(comment.id, sugg)}
            onIgnore={() => ignoreSuggestion(comment.id)}
          />
        );
      },
    }),
    [
      createComment,
      updateComment,
      deleteComment,
      isDeleting,
      resolveComment,
      resolveAgentIdentity,
      projectOwner,
      brief,
      applySuggestion,
      ignoreSuggestion,
      triggerAgentResponse,
    ],
  );

  const body = (
    <CommentsPanel
      isLoading={isLoading}
      showResolved={showResolved}
      onToggleResolved={() => setShowResolved(!showResolved)}
      hasComments={totalAll > 0}
      sortOrder={rootSortOrder}
      onSortChange={setRootSortOrder}
      sortable={rootComments.length > 1}
      isEmpty={isEmpty}
      emptyTitle={
        hasHiddenResolved
          ? t('projectBriefDetail.briefCommentsSidebar.noPendingComments')
          : t('projectBriefDetail.briefCommentsSidebar.noCommentLeftYet')
      }
      emptyDescription={
        hasHiddenResolved ? (
          <Trans
            i18nKey="projectBriefDetail.briefCommentsSidebar.toggleResolvedCommentsToSee"
            ns="projects"
            components={{ br: <br /> }}
          />
        ) : (
          <Trans
            i18nKey="projectBriefDetail.briefCommentsSidebar.reviewTheDocumentAndType"
            ns="projects"
            components={{ br: <br /> }}
          />
        )
      }
      hasMore={hasMore}
      isFetchingMore={isFetchingMore}
      onLoadMore={loadMore}
      footer={
        <ScopedPermission
          permissions={[PROJECT_PERMISSIONS.ProjectComment]}
          fallback={
            <div className="rounded-lg border border-border-light px-3 py-2.5 text-sm text-muted-foreground">
              {t('projectBriefDetail.briefCommentsSidebar.addAComment')}
            </div>
          }
        >
          <AnnotatedCommentInput
            placeholder={t('projectBriefDetail.briefCommentsSidebar.addAComment')}
            mentionOptions={mentionOptions}
            showAnnotationToolbar={false}
            isDefaultComposer
            composerPrefix={composerAnchorChip}
            // Only the section anchor takes the caret. A text-range anchor is created by
            // dragging a selection in the document, and pulling focus mid-selection would cut
            // that gesture short.
            focusKey={pendingFieldAnchor?.sectionId ?? null}
            onSave={async (content, _annotation, attachmentAssetIds) => {
              const anchor = pendingTextAnchor;
              // A typed-field comment ("Comment on section" in `BriefSectionShell`) is
              // mutually exclusive with a text-highlight anchor — a field has no document
              // range to highlight. Text takes priority if somehow both are set.
              const fieldAnchor = anchor ? null : pendingFieldAnchor;
              const created = await createComment(content, {
                attachmentAssetIds,
                ...(anchor
                  ? {
                      annotation: {
                        kind: 'text',
                        sectionId: anchor.sectionId,
                        from: anchor.from,
                        to: anchor.to,
                      },
                    }
                  : fieldAnchor
                    ? { annotation: { kind: 'field', sectionId: fieldAnchor.sectionId } }
                    : {}),
              });
              const selection = anchor
                ? { selectedText: anchor.selectedText, sectionId: anchor.sectionId }
                : undefined;
              if (anchor) {
                anchor.applyMark(created.id);
                clearPendingTextAnchor();
              } else if (fieldAnchor) {
                clearPendingFieldAnchor();
              }
              // A tagged root comment threads the agent's reply under itself.
              triggerAgentResponse(created, undefined, selection);
              setPendingScrollId(created.id);
            }}
          />
        </ScopedPermission>
      }
    >
      {/* Each row carries a ref so a focused text-anchor highlight can scroll
          its comment into view (brief-only; the file panel uses `data-comment-id`). */}
      {rootComments.map((comment) => (
        <div
          key={comment.id}
          ref={(el) => {
            if (el) commentRowRefs.current.set(comment.id, el);
            else commentRowRefs.current.delete(comment.id);
            registerRow(comment.id)(el);
          }}
        >
          <BriefCommentThread comment={comment} actions={actions} mentionOptions={mentionOptions} />
        </div>
      ))}
    </CommentsPanel>
  );

  return body;
}
